import type { NotificationSetting } from '@modules/notifications/notificationChannels';
import type { Translate } from '@shared/hooks/locale';
import { getModuleLabel } from '@shared/utils/module-label';

export type NotificationGroup = {
  module: string | null;
  settings: NotificationSetting[];
};

const compareSettings = (a: NotificationSetting, b: NotificationSetting) =>
  a.order_nr - b.order_nr || a.type.localeCompare(b.type);

export const groupNotificationsByModule = (
  settings: NotificationSetting[],
  moduleIds: readonly string[],
): NotificationGroup[] => {
  const byModule = new Map<string | null, NotificationSetting[]>();

  settings.forEach(setting => {
    const key =
      setting.modules?.find(module => moduleIds.includes(module)) ?? null;
    byModule.set(key, [...(byModule.get(key) ?? []), setting]);
  });

  return [...byModule.entries()]
    .map(([module, moduleSettings]) => ({
      module,
      settings: [...moduleSettings].sort(compareSettings),
    }))
    .sort((a, b) => {
      if (a.module == null || b.module == null) {
        return Number(b.module == null) - Number(a.module == null);
      }

      return (
        a.settings[0].order_nr - b.settings[0].order_nr ||
        a.module.localeCompare(b.module)
      );
    });
};

export const humanizeModuleKey = (module: string): string => {
  const words = module.replace(/[_-]+/g, ' ').trim();

  return words.charAt(0).toUpperCase() + words.slice(1);
};

const GENERAL_GROUP = '__general__';

export const getGroupKey = (group: NotificationGroup): string =>
  group.module ?? GENERAL_GROUP;

export const getCollapsedGroupKeys = (
  groups: readonly NotificationGroup[],
  focusGroup: string | null,
): string[] => {
  const keys = groups.map(getGroupKey);

  return focusGroup == null || !keys.includes(focusGroup)
    ? []
    : keys.filter(key => key !== focusGroup);
};

export const getGroupHeading = (
  module: string | null,
  t: Translate,
): string => {
  if (module == null) {
    return t('General');
  }

  const label = getModuleLabel(module, t);

  return label === module ? humanizeModuleKey(module) : label;
};
